import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Warning, ShieldCheck, Clock, FileText, Download } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { FONCTIONS } from '../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignaturePublicPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [consent, setConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);
  const [showRefuse, setShowRefuse] = useState(false);
  const [refuseMotif, setRefuseMotif] = useState('');
  const [geolocation, setGeolocation] = useState(null);
  const inputsRef = useRef([]);

  useEffect(() => {
    load();
  }, [token]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/signer/${token}`);
      setData(res.data);
      if (res.data.statut === 'signe') setDone({ kind: 'signe', at: res.data.signe_le });
      if (res.data.statut === 'refuse') setDone({ kind: 'refuse', at: res.data.refuse_le });
    } catch (e) {
      setError(e.response?.data?.detail || 'Lien de signature invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!data) return;
    try {
      setSendingCode(true);
      const { data: envoi } = await axios.post(`${API_URL}/api/signatures/${data.sig_id}/envoyer-code/${data.signataire_id}`, { token });
      // Fluidité B4 (B15, P5#9) — si le SMS n'a pas pu partir, le code arrive par e-mail
      // et l'architecte est prévenu : un message simple, jamais d'erreur technique.
      if (envoi?.canal === 'email') {
        setData((d) => ({ ...d, code_canal: 'email', email_masque: envoi.email_masque || d.email_masque }));
        toast.info(envoi.message || 'Le SMS n\'a pas pu partir : votre code a été envoyé par e-mail.', { duration: 8000 });
      } else {
        toast.success(`Code envoyé au ${data.telephone_masque}`);
      }
      setCodeSent(true);
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
    } catch (e) {
      // Le serveur ne renvoie qu'un message lisible ; jamais le détail technique.
      toast.error(e.response?.data?.detail || "Le SMS n'a pas pu partir, votre architecte est prévenu.");
    } finally {
      setSendingCode(false);
    }
  };

  const handleCodeChange = (i, val) => {
    const digit = (val || '').replace(/\D/g, '').slice(-1);
    const next = [...code];
    next[i] = digit;
    setCode(next);
    if (digit && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const handleCodeKey = (i, e) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const handleCodePaste = (e) => {
    const paste = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      e.preventDefault();
      setCode(paste.split(''));
      inputsRef.current[5]?.focus();
    }
  };

  const handleGeoRequest = () => {
    if (!navigator.geolocation) return toast.error('Géolocalisation non disponible');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeolocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        toast.success('Position enregistrée');
      },
      () => toast.error('Accès refusé'),
      { timeout: 10000 },
    );
  };

  const handleSign = async () => {
    const codeStr = code.join('');
    if (codeStr.length !== 6) return toast.error('Code à 6 chiffres requis');
    if (!consent) return toast.error('Vous devez accepter le consentement');
    try {
      setSigning(true);
      const payload = { code: codeStr, consent: true };
      if (geolocation) payload.geolocation = geolocation;
      const res = await axios.post(`${API_URL}/api/signer/${token}/valider`, payload);
      setDone({ kind: 'signe', at: res.data.signe_le, all_signed: res.data.all_signed });
      toast.success('Signature enregistrée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur signature');
    } finally {
      setSigning(false);
    }
  };

  const handleRefuse = async () => {
    if (!refuseMotif.trim()) return toast.error('Motif requis');
    try {
      await axios.post(`${API_URL}/api/signer/${token}/refuser`, { motif: refuseMotif });
      setDone({ kind: 'refuse', at: new Date().toISOString() });
      toast.success('Refus enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return <FullPage><div className="text-center py-20"><Clock size={48} className="mx-auto text-[#C4773B] animate-spin" /><p className="mt-4 text-[#6B6B6B]">Chargement du document...</p></div></FullPage>;
  }

  if (error) {
    return <FullPage><ErrorBlock icon={XCircle} title="Lien invalide" message={error} /></FullPage>;
  }

  if (data?.statut === 'expire' || done?.kind === 'expire') {
    return <FullPage><ErrorBlock icon={Warning} title="Demande expirée" message={`La demande de signature "${data?.titre || ''}" a expiré. Contactez l'expéditeur pour une nouvelle demande.`} /></FullPage>;
  }

  if (data?.statut === 'annule') {
    return <FullPage><ErrorBlock icon={XCircle} title="Demande annulée" message={`La demande de signature "${data?.titre || ''}" a été annulée par son auteur.`} /></FullPage>;
  }

  if (done?.kind === 'signe') {
    return (
      <FullPage>
        <div className="text-center py-12" data-testid="signature-success">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#E8F5E9] mb-6 rounded-full">
            <CheckCircle size={56} weight="fill" className="text-[#2D6A4F]" />
          </div>
          <h1 className="text-3xl mb-3 text-[#1E3A5F]" style={{ fontFamily: 'DM Serif Display, serif' }}>Document signé avec succès</h1>
          <p className="text-[#6B6B6B] mb-2">Votre signature électronique a été enregistrée le</p>
          <p className="text-[#2C2C2C] font-medium mb-6">{new Date(done.at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}</p>
          {done.all_signed ? (
            <div className="bg-[#E8F5E9] border border-[#2D6A4F] p-4 max-w-md mx-auto text-sm text-[#1B4332]">
              <ShieldCheck size={24} className="mx-auto mb-2" weight="bold" />
              Toutes les signatures requises ont été recueillies. Le demandeur en a été notifié.
            </div>
          ) : (
            <div className="bg-[#FFF8E1] border border-[#B8860B] p-4 max-w-md mx-auto text-sm text-[#7A5A00]">
              En attente des autres signataires ({(data?.nb_signes || 0) + 1}/{data?.nb_signataires || 0})
            </div>
          )}
          <p className="mt-8 text-xs text-[#6B6B6B] max-w-xl mx-auto">
            {FONCTIONS.signatureSms} (Code civil français, articles 1366-1367).
            Vous pouvez fermer cette page.
          </p>
        </div>
      </FullPage>
    );
  }

  if (done?.kind === 'refuse' || data?.statut === 'refuse') {
    return (
      <FullPage>
        <div className="text-center py-12">
          <XCircle size={56} weight="fill" className="mx-auto text-[#8B3A3A] mb-6" />
          <h1 className="text-3xl mb-3 text-[#1E3A5F]" style={{ fontFamily: 'DM Serif Display, serif' }}>Refus enregistré</h1>
          <p className="text-[#6B6B6B]">Vous avez refusé de signer ce document. Le demandeur en sera notifié.</p>
        </div>
      </FullPage>
    );
  }

  // ========= Mode signature actif =========
  return (
    <FullPage>
      {/* En-tête */}
      <div className="border-b border-[#E0E0DB] pb-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={20} weight="bold" className="text-[#C4773B]" />
          <span className="text-xs uppercase tracking-wider text-[#C4773B] font-medium">{FONCTIONS.signatureSms}</span>
        </div>
        <h1 className="text-3xl text-[#1E3A5F] mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>{data.titre}</h1>
        {data.chantier_nom && (
          <p className="text-sm text-[#6B6B6B]">
            Chantier : <span className="text-[#2C2C2C]">{data.chantier_nom}</span>
            {data.chantier_adresse && <> — {data.chantier_adresse}</>}
          </p>
        )}
        <p className="text-sm text-[#6B6B6B] mt-1">
          Demandé par <span className="text-[#2C2C2C]">{data.demande_par}</span> le {new Date(data.demande_le).toLocaleDateString('fr-FR')}
        </p>
      </div>

      {/* Signataire */}
      <div className="bg-[#F8F7F4] border-l-4 border-[#C4773B] p-4 mb-6">
        <p className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-1">Signataire</p>
        <p className="text-lg font-medium text-[#2C2C2C]">{data.signataire_nom}</p>
        {data.signataire_role && <p className="text-sm text-[#6B6B6B]">{data.signataire_role}</p>}
        <p className="text-xs text-[#6B6B6B] mt-2">
          {data.nb_signes} / {data.nb_signataires} signature{data.nb_signataires > 1 ? 's' : ''} déjà recueillie{data.nb_signes > 1 ? 's' : ''}
        </p>
      </div>

      {/* Contenu du document */}
      <div className="mb-6">
        <h2 className="text-sm uppercase tracking-wider text-[#6B6B6B] mb-3 flex items-center gap-2">
          <FileText size={16} />Document à signer
        </h2>
        <DocumentPreview content={data.document_content} apiUrl={API_URL} />
        <p className="text-xs text-[#6B6B6B] mt-2 font-mono break-all" title="Empreinte SHA-256">
          SHA-256 : {data.document_hash}
        </p>
      </div>

      {/* Consentement */}
      <label className="flex items-start gap-3 bg-[#FFF8E1] border border-[#B8860B] p-4 mb-6 cursor-pointer" data-testid="consent-checkbox">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-5 h-5 accent-[#C4773B]" />
        <span className="text-sm text-[#2C2C2C]">
          Je certifie avoir pris connaissance du document ci-dessus et je l'approuve en signant électroniquement.
          Je comprends que cette signature a la même valeur juridique qu'une signature manuscrite (art. 1366-1367 du Code civil).
        </span>
      </label>

      {/* Étape 1 : Envoi code */}
      {!codeSent ? (
        <div className="text-center py-8 border border-[#E0E0DB] bg-[#FAFAF8]">
          <p className="text-sm text-[#6B6B6B] mb-4">Pour signer, vous allez recevoir un code à 6 chiffres par SMS au <strong>{data.telephone_masque}</strong></p>
          <Button
            onClick={handleSendCode}
            disabled={!consent || sendingCode}
            className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none px-8 py-6 text-base"
            data-testid="send-code-btn"
          >
            {sendingCode ? 'Envoi en cours...' : '📱 Recevoir mon code par SMS'}
          </Button>
          {!consent && <p className="text-xs text-[#8B3A3A] mt-3">Veuillez d'abord accepter le consentement ci-dessus</p>}
        </div>
      ) : (
        <div className="border border-[#C4773B] bg-white p-6">
          <p className="text-sm text-[#2C2C2C] mb-4 text-center" data-testid="code-canal">
            {data.code_canal === 'email'
              ? <>Le SMS n'a pas pu partir. Saisissez le code à 6 chiffres reçu par e-mail à <strong>{data.email_masque}</strong></>
              : <>Saisissez le code à 6 chiffres reçu par SMS au <strong>{data.telephone_masque}</strong></>}
          </p>
          <div className="flex justify-center gap-2 mb-4" onPaste={handleCodePaste}>
            {code.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleCodeChange(i, e.target.value)}
                onKeyDown={(e) => handleCodeKey(i, e)}
                className="w-12 h-14 border-2 border-[#E0E0DB] focus:border-[#C4773B] text-center text-2xl font-bold tabular-nums outline-none"
                data-testid={`code-input-${i}`}
              />
            ))}
          </div>
          <p className="text-xs text-center text-[#6B6B6B] mb-4">Le code expire dans 10 minutes · 3 tentatives max</p>

          {/* Géolocalisation optionnelle */}
          <div className="text-center mb-4">
            {geolocation ? (
              <p className="text-xs text-[#2D6A4F]">✓ Position GPS enregistrée ({geolocation.latitude.toFixed(4)}, {geolocation.longitude.toFixed(4)})</p>
            ) : (
              <button onClick={handleGeoRequest} className="text-xs text-[#6B6B6B] underline hover:text-[#C4773B]">
                Optionnel : partager ma position pour renforcer la preuve
              </button>
            )}
          </div>

          <Button
            onClick={handleSign}
            disabled={signing || code.join('').length !== 6 || !consent}
            className="w-full bg-[#2D6A4F] hover:bg-[#1E4A35] rounded-none py-6 text-base text-white"
            data-testid="sign-btn"
          >
            <CheckCircle size={20} weight="bold" className="mr-2" />
            {signing ? 'Signature en cours...' : 'Signer électroniquement'}
          </Button>

          <div className="text-center mt-3">
            <button onClick={handleSendCode} className="text-xs text-[#C4773B] hover:underline" disabled={sendingCode}>
              Renvoyer un nouveau code
            </button>
          </div>
        </div>
      )}

      {/* Refus */}
      <div className="mt-8 text-center">
        {!showRefuse ? (
          <button onClick={() => setShowRefuse(true)} className="text-sm text-[#8B3A3A] hover:underline" data-testid="open-refuse-btn">
            Refuser de signer ce document
          </button>
        ) : (
          <div className="border border-[#8B3A3A] bg-[#FFEBEE] p-4 text-left">
            <p className="text-sm text-[#8B3A3A] mb-2 font-medium">Motif du refus</p>
            <textarea
              value={refuseMotif}
              onChange={(e) => setRefuseMotif(e.target.value)}
              rows={3}
              className="w-full border border-[#E0E0DB] p-2 text-sm"
              placeholder="Expliquez brièvement votre refus..."
              data-testid="refuse-motif"
            />
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="outline" onClick={() => setShowRefuse(false)} className="rounded-none" size="sm">Annuler</Button>
              <Button onClick={handleRefuse} className="bg-[#8B3A3A] hover:bg-[#6A2A2A] text-white rounded-none" size="sm" data-testid="confirm-refuse-btn">
                Confirmer le refus
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Mention légale */}
      <div className="mt-10 pt-6 border-t border-[#E0E0DB] text-[11px] text-[#6B6B6B] leading-relaxed">
        <p>Signature électronique par code SMS, au sens des articles 1366 et 1367 du Code civil français. Les données enregistrées (IP, horodatage UTC, empreinte SHA-256, numéro de téléphone vérifié par SMS OTP) constituent un faisceau de preuves conservé pour la valeur probante.</p>
      </div>
    </FullPage>
  );
}

function FullPage({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAF8] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-block text-2xl text-[#1E3A5F]" style={{ fontFamily: 'DM Serif Display, serif', letterSpacing: '0.1em' }}>
            KROQUIS
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#6B6B6B] mt-1">Signature électronique</div>
        </div>
        <div className="bg-white border border-[#E0E0DB] p-8 shadow-sm">
          {children}
        </div>
        <p className="text-center text-[10px] text-[#6B6B6B] mt-4">
          Kroquis — {FONCTIONS.signatureSms}
        </p>
      </div>
    </div>
  );
}

function ErrorBlock({ icon: Icon, title, message }) {
  return (
    <div className="text-center py-12">
      <Icon size={48} weight="fill" className="mx-auto text-[#8B3A3A] mb-4" />
      <h1 className="text-2xl mb-2 text-[#1E3A5F]" style={{ fontFamily: 'DM Serif Display, serif' }}>{title}</h1>
      <p className="text-[#6B6B6B]">{message}</p>
    </div>
  );
}

function DocumentPreview({ content, apiUrl }) {
  if (!content) {
    return <div className="bg-[#F5F5F3] border border-[#E0E0DB] p-6 text-sm text-[#6B6B6B] italic">Document non disponible</div>;
  }

  if (content.kind === 'crc') {
    return (
      <div className="bg-white border border-[#E0E0DB] p-6 max-h-[500px] overflow-y-auto" data-testid="doc-preview">
        <div className="mb-4 pb-3 border-b border-[#E0E0DB]">
          <p className="font-medium text-[#2C2C2C]">{content.titre}</p>
          {content.periode?.debut && (
            <p className="text-xs text-[#6B6B6B]">Période : {content.periode.debut} → {content.periode.fin}</p>
          )}
        </div>
        <div className="text-sm text-[#2C2C2C] leading-relaxed whitespace-pre-wrap" style={{ fontFamily: 'Georgia, serif' }}>
          {content.markdown || 'Contenu non disponible.'}
        </div>
      </div>
    );
  }

  // RP49 — PV de réception : réserves par lot (registre public), PDF consultable.
  if (content.kind === 'pv_reception') {
    const dateFr = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR') : '');
    return (
      <div className="bg-white border border-[#E0E0DB] p-6 max-h-[500px] overflow-y-auto" data-testid="doc-preview-pv-reception">
        <div className="mb-4 pb-3 border-b border-[#E0E0DB] flex items-start justify-between gap-3">
          <div>
            <p className="font-medium text-[#2C2C2C]">Procès-verbal de réception des travaux</p>
            <p className="text-xs text-[#6B6B6B]">
              Visite du {dateFr(content.date_reception)} · réception prononcée à effet du {dateFr(content.date_effet)}
            </p>
            <p className="text-sm text-[#2C2C2C] mt-1">
              {content.avec_reserves
                ? `Avec ${content.nb_reserves} réserve(s) : défauts à corriger par les entreprises.`
                : 'Sans réserve.'}
            </p>
          </div>
          {content.pdf_url && (
            <a href={`${apiUrl}${content.pdf_url}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#C4773B] underline whitespace-nowrap" data-testid="pv-reception-pdf">
              <Download size={14} /> Voir le PDF
            </a>
          )}
        </div>
        {(content.lots || []).map((l) => (
          <div key={l.code} className="mb-3">
            <p className="text-sm font-medium text-[#1E3A5F]">
              Lot {l.code}{l.nom && l.nom !== l.code ? ` — ${l.nom}` : ''}{l.entreprise ? ` · ${l.entreprise}` : ''}
            </p>
            {(l.reserves || []).length === 0 ? (
              <p className="text-xs text-[#6B6B6B]">Sans réserve.</p>
            ) : (
              <ul className="text-sm text-[#2C2C2C] list-disc pl-5">
                {l.reserves.map((r) => (
                  <li key={r.numero}>
                    {r.description}
                    {r.localisation && <span className="text-[#6B6B6B]"> ({r.localisation})</span>}
                    {r.echeance && <span className="text-[#6B6B6B]"> · à corriger avant le {dateFr(r.echeance)}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {content.observations && <p className="text-xs text-[#6B6B6B] whitespace-pre-wrap">{content.observations}</p>}
      </div>
    );
  }

  if (content.kind === 'document') {
    return (
      <div className="bg-white border border-[#E0E0DB] p-6 max-h-[500px] overflow-y-auto" data-testid="doc-preview">
        <div className="mb-4 pb-3 border-b border-[#E0E0DB] flex items-center justify-between">
          <div>
            <p className="font-medium text-[#2C2C2C]">{content.nom}</p>
            <p className="text-xs text-[#6B6B6B]">Type : {content.type || 'document'}</p>
          </div>
          <FileText size={32} className="text-[#C4773B]" />
        </div>
        {content.texte_extrait ? (
          <div className="text-xs text-[#2C2C2C] leading-relaxed whitespace-pre-wrap font-mono bg-[#FAFAF8] p-3" style={{ maxHeight: 300, overflow: 'auto' }}>
            {content.texte_extrait}
            {content.texte_extrait.length >= 5000 && <div className="mt-2 text-[#6B6B6B] italic">[... contenu tronqué ...]</div>}
          </div>
        ) : (
          <p className="text-sm text-[#6B6B6B] italic">Document sans extraction textuelle disponible.</p>
        )}
      </div>
    );
  }

  return null;
}
